import { useTranslation } from 'react-i18next'
import { formatCurrency, formatNumber } from '@/lib/format'
import { cn } from '@/lib/cn'

export interface AccountSnapshot {
  balance: number
  margin: number
  freeMargin: number
  equity: number
  pnl: number
  marginLevel: number
  currency?: string
}

const DEMO_ACCOUNT: AccountSnapshot = {
  balance: 125_480.55,
  margin: 18_240.0,
  freeMargin: 107_240.55,
  equity: 128_112.3,
  pnl: 2_631.75,
  marginLevel: 702.37,
  currency: 'USD',
}

function Metric({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'bid' | 'ask' | 'neutral'
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-2.5 first:ps-0 last:pe-0">
      <span className="truncate text-[9px] font-semibold uppercase tracking-wider text-terminal-muted">
        {label}
      </span>
      <span
        className={cn(
          'truncate font-mono text-[11px] font-semibold tabular-nums',
          tone === 'bid' && 'text-bid',
          tone === 'ask' && 'text-ask',
          (!tone || tone === 'neutral') && 'text-terminal-fg',
        )}
      >
        {value}
      </span>
    </div>
  )
}

/**
 * Header-center account summary strip — Balance, Margin, Equity, P&L, etc.
 */
export function AccountMetrics({
  data = DEMO_ACCOUNT,
  className,
}: {
  data?: AccountSnapshot
  className?: string
}) {
  const { t } = useTranslation('common')
  const ccy = data.currency ?? 'USD'
  const pnlTone = data.pnl >= 0 ? 'bid' : 'ask'

  return (
    <div
      className={cn(
        'hidden min-w-0 flex-1 items-stretch justify-center lg:flex',
        className,
      )}
    >
      <div
        className={cn(
          'flex max-w-3xl flex-1 items-center divide-x divide-terminal-border/40',
          'overflow-hidden rounded-sm border border-terminal-border/40',
          'bg-terminal-surface/70 px-1 py-1 backdrop-blur-sm',
        )}
      >
        <Metric
          label={t('account.balance')}
          value={formatCurrency(data.balance, ccy)}
        />
        <Metric
          label={t('account.margin')}
          value={formatCurrency(data.margin, ccy)}
        />
        <Metric
          label={t('account.freeMargin')}
          value={formatCurrency(data.freeMargin, ccy)}
        />
        <Metric
          label={t('account.equity')}
          value={formatCurrency(data.equity, ccy)}
        />
        <Metric
          label={t('account.pnl')}
          value={formatCurrency(data.pnl, ccy)}
          tone={pnlTone}
        />
        <Metric
          label={t('account.marginLevel')}
          value={`${formatNumber(data.marginLevel, { maximumFractionDigits: 2 })}%`}
          tone={data.marginLevel < 100 ? 'ask' : 'neutral'}
        />
      </div>
    </div>
  )
}
